import { createContext, useCallback, useContext, useRef, useState } from 'react';
import Button from './Button';
import { useDialogFocus } from './Modal';

// §2.6 Confirm — replaces window.confirm. useConfirm()(options) resolves true/false.
// options: { title, body, confirmLabel, cancelLabel, destructive, typed } — destructive is two-step
// (the danger button fills only on the confirm step); typed: a word the user must type to enable it.
const Ctx = createContext(null);

export function ConfirmProvider({ children }) {
  const [req, setReq] = useState(null);
  const [armed, setArmed] = useState(false);
  const [typedText, setTypedText] = useState('');
  const box = useRef(null);
  const confirm = useCallback(opts => new Promise(resolve => { setArmed(false); setTypedText(''); setReq({ ...opts, resolve }); }), []);
  const close = useCallback(ok => { setReq(r => { r?.resolve(ok); return null; }); }, []);
  useDialogFocus(!!req, box, () => close(false));
  const needsTyping = req?.typed && typedText.trim() !== req.typed;
  return (
    <Ctx.Provider value={confirm}>
      {children}
      {req && (
        <div className="ui ui-scrim" onMouseDown={e => { if (e.target === e.currentTarget) close(false); }}>
          <section className="ui-modal" role="alertdialog" aria-modal="true" aria-labelledby="ui-confirm-title" aria-describedby="ui-confirm-body" ref={box}>
            <header className="ui-modal__head"><h2 className="ui-modal__title" id="ui-confirm-title">{req.title}</h2></header>
            <div className="ui-modal__body" id="ui-confirm-body">
              {req.body && <p className="ui-state__msg">{req.body}</p>}
              {req.typed && (
                <label className="ui-field">
                  <span className="ui-field__label">Type {req.typed} to confirm</span>
                  <input className="ui-input" value={typedText} onChange={e => setTypedText(e.target.value)} autoComplete="off" data-autofocus />
                </label>
              )}
            </div>
            <footer className="ui-modal__foot">
              <Button variant="quiet" onClick={() => close(false)}>{req.cancelLabel || 'Cancel'}</Button>
              {req.destructive
                ? (armed
                  ? <Button variant="danger" confirmStep disabled={needsTyping} onClick={() => close(true)}>{req.confirmLabel || 'Delete'}</Button>
                  : <Button variant="danger" disabled={needsTyping} onClick={() => setArmed(true)}>{req.confirmLabel || 'Delete'}</Button>)
                : <Button variant="primary" disabled={needsTyping} onClick={() => close(true)}>{req.confirmLabel || 'Confirm'}</Button>}
            </footer>
          </section>
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useConfirm() {
  const fn = useContext(Ctx);
  if (!fn) throw new Error('useConfirm needs <ConfirmProvider> at the app root');
  return fn;
}
